const skFsbGiftPropertyKeys = {
  autoAdded: "_sk_free_gift_auto",
  ruleId: "_sk_free_gift_rule",
  source: "_sk_free_gift_source",
};

const skFsbGiftSource = "cart_value";

class HCFsb {
  constructor(fsb = { selector: "#hc-fsb", events: [], placeholder: "(x)" }) {
    this.validateConfig(fsb);
    this.initializeProperties(fsb);
    if (!this.registerCheckoutVerifier()) {
      this.attachCheckoutEvent();
    }
    this.attachEvents();
  }

  validateConfig(fsb) {
    if (!fsb.selector || typeof fsb.selector !== "string") {
      throw new TypeError("Please pass a valid selector");
    }
  }

  initializeProperties(fsb) {
    this.selector = fsb.selector;
    this.fsbElementContainer = document.querySelector(this.selector);
    this.fsbElement = this.fsbElementContainer?.querySelector(".fsb");

    if (!this.fsbElement) {
      throw new TypeError("Freeshipping Bar Element Not Found");
    }

    this.events = fsb.events;
    this.placeholder = fsb.placeholder;
    this.shippingGoal = parseInt(this.fsbElement.dataset.shippingGoal);
    this.maxGoal = parseInt(this.fsbElement.dataset.maxGoal) || this.shippingGoal;
    this.startHeadline = this.fsbElement.dataset.headlineStart;
    this.endHeadline = this.fsbElement.dataset.headlineEnd;

    this.middleHeadlines = [];
    this.freeGifts = [];

    for (let i = 1; i <= 3; i++) {
      const goal = parseInt(this.fsbElement.dataset[`freeGift${i}Goal`]);
      const product = parseInt(this.fsbElement.dataset[`freeGift${i}`]);
      const title = this.fsbElement.dataset[`freeGiftTitle${i}`];
      const headline = this.fsbElement.dataset[`headlineMiddle${i}`];

      if (goal && product && title && headline) {
        this.freeGifts.push({
          goal,
          product,
          title,
          ruleKey: `cart_value:${goal}:${product}`,
        });
        this.middleHeadlines.push(headline);
      }
    }

    this.fsText = this.fsbElement.dataset.fsText;
  }

  async getCart() {
    try {
      const response = await fetch("/cart.js");
      return await response.json();
    } catch (error) {
      console.error("Error fetching cart:", error);
      throw error;
    }
  }

  getBaseTotal(cart) {
    const giftIds = this.freeGifts.map((g) => g.product);
    const excludedHandles = [
      "delivery-fee-hk-island",
      "delivery-fee-kowloon",
      "express-checkout",
      "express-delivery-kowloon",
    ];
    return cart.items.reduce((sum, item) => {
      if (item.requires_shipping === false || giftIds.includes(item.id) || excludedHandles.includes(item.handle)) {
        return sum;
      }
      return sum + item.line_price;
    }, 0);
  }

  async postJson(url, payload) {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error("Cart request failed");
    }

    return response.json();
  }

  async update() {
    try {
      const cart = await this.getCart();
      const baseTotal = this.getBaseTotal(cart);

      let changed = false;

      for (const gift of this.freeGifts) {
        const inCart = this.isProductInCart(cart, gift.product);
        const eligible = baseTotal >= gift.goal;

        if (eligible && !inCart) {
          changed = (await this.addFreeGift(gift)) || changed;
        } else if (!eligible && inCart) {
          changed = (await this.removeFreeGift(gift.product)) || changed;
        }
      }

      if (changed) {
        location.reload();
        return;
      }

      const virtualCart = { ...cart, total_price: baseTotal };

      if (baseTotal <= 0) {
        this.fsbElementContainer.hidden = true;
        return;
      }

      this.fsbElementContainer.hidden = false;
      this.updateProgressBar(virtualCart);
      this.updateHeadline(virtualCart);
    } catch (error) {
      console.error("Error updating FSB:", error);
    }
  }

  updateProgressBar(cart) {
    if (!this.maxGoal || this.maxGoal <= 0) {
      return;
    }
    let progress = (cart.total_price * 100) / this.maxGoal;
    if (progress > 100) progress = 100;
    if (progress < 0) progress = 0;
    this.fsbElement.style.setProperty("--progress", `${progress}%`);
  }

  updateHeadline(cart) {
    const headline = this.fsbElementContainer.querySelector(".fsb-paragraph");

    if (cart.total_price > 0 && cart.total_price < this.shippingGoal) {
      const shippingDifference = this.shippingGoal - cart.total_price;
      headline.innerHTML = this.startHeadline
        .replace(
          this.placeholder,
          `<span class="fsb--number">${this.formatMoney(
            shippingDifference
          )}</span>`
        )
        .replace("(goal)", this.fsText);
      return;
    }

    if (this.freeGifts.length === 0) {
      headline.innerHTML = this.endHeadline || "Free Shipping Reached!";
      return;
    }

    for (let i = 0; i < this.freeGifts.length; i++) {
      const gift = this.freeGifts[i];
      const difference = gift.goal - cart.total_price;

      if (cart.total_price < gift.goal) {
        headline.innerHTML = this.middleHeadlines[i]
          .replace(
            this.placeholder,
            `<span class="fsb--number">${this.formatMoney(difference)}</span>`
          )
          .replace("(item)", gift.title);
        return;
      }
    }

    const lastGift = this.freeGifts[this.freeGifts.length - 1];
    if (cart.total_price >= lastGift.goal) {
      headline.innerHTML = this.endHeadline.replace("(item)", lastGift.title);
    }
  }

  attachEvents() {
    if (this.events.length > 0) {
      this.events.forEach((event) => {
        document.addEventListener(event, () => this.update(), true);
      });
    }
  }

  formatMoney(cents, format) {
    if (typeof cents === "string") {
      cents = cents.replace(".", "");
    }
    const value = "";
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || "$" + "{".repeat(2) + "amount_no_decimals" + "}".repeat(2);

    const formatWithDelimiters = (
      number,
      precision = 2,
      thousands = ",",
      decimal = "."
    ) => {
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      const parts = number.split(".");
      const dollars = parts[0].replace(
        /(\d)(?=(\d\d\d)+(?!\d))/g,
        `$1${thousands}`
      );
      return dollars + (parts[1] ? decimal + parts[1] : "");
    };

    const moneyFormat = formatString.match(placeholderRegex)?.[1];
    return formatString.replace(
      placeholderRegex,
      formatWithDelimiters(cents, moneyFormat === "amount_no_decimals" ? 0 : 2)
    );
  }

  getGiftProperties(gift) {
    return {
      [skFsbGiftPropertyKeys.autoAdded]: "true",
      [skFsbGiftPropertyKeys.ruleId]: gift.ruleKey,
      [skFsbGiftPropertyKeys.source]: skFsbGiftSource,
    };
  }

  async addFreeGift(gift) {
    const formData = {
      items: [
        {
          id: gift.product,
          quantity: parseInt(gift.quantity, 10) || 1,
          properties: this.getGiftProperties(gift),
        },
      ],
    };
    try {
      await this.postJson("/cart/add.js", formData);
      return true;
    } catch (error) {
      console.error("Error adding gift:", error);
      return false;
    }
  }

  async removeFreeGift(product) {
    const updates = { [product]: 0 };
    try {
      await this.postJson("/cart/update.js", { updates });
      return true;
    } catch (error) {
      console.error("Error removing gift:", error);
      return false;
    }
  }

  isProductInCart(cart, productId) {
    return cart.items.some((item) => item.id === productId);
  }

  getGiftLines(cart, gift) {
    return cart.items.filter((item) => item.id === gift.product);
  }

  getGiftQuantity(cart, gift) {
    return this.getGiftLines(cart, gift).reduce((total, item) => {
      return total + (parseInt(item.quantity, 10) || 0);
    }, 0);
  }

  getGiftIssue(cart, gift) {
    const baseTotal = this.getBaseTotal(cart);
    const expected = baseTotal >= gift.goal ? 1 : 0;
    const actual = this.getGiftQuantity(cart, gift);

    if (expected === actual) {
      return null;
    }

    return {
      source: skFsbGiftSource,
      type: expected > actual ? "missing" : "excess",
      ruleKey: gift.ruleKey,
      ruleName: `Cart value ${gift.goal}`,
      giftTitle: gift.title,
      expected,
      actual,
    };
  }

  getGiftIssues(cart) {
    return this.freeGifts.map((gift) => this.getGiftIssue(cart, gift)).filter(Boolean);
  }

  async setGiftQuantity(cart, gift, targetQuantity) {
    const lines = this.getGiftLines(cart, gift);
    const updates = {};
    let quantityToKeep = targetQuantity;
    let changed = false;

    lines.forEach((line) => {
      const lineQuantity = parseInt(line.quantity, 10) || 0;
      const nextQuantity = Math.max(Math.min(lineQuantity, quantityToKeep), 0);
      quantityToKeep = Math.max(quantityToKeep - nextQuantity, 0);

      if (nextQuantity !== lineQuantity) {
        updates[line.key] = nextQuantity;
        changed = true;
      }
    });

    if (Object.keys(updates).length) {
      await this.postJson("/cart/update.js", { updates });
    }

    if (quantityToKeep > 0) {
      const added = await this.addFreeGift({ ...gift, quantity: quantityToKeep });
      changed = added || changed;
    }

    return changed;
  }

  async verifyCheckout({ cart, repair }) {
    if (!this.freeGifts.length) {
      return { changed: false, issues: [] };
    }

    const workingCart = cart || (await this.getCart());

    if (!repair) {
      return {
        changed: false,
        issues: this.getGiftIssues(workingCart),
      };
    }

    let changed = false;

    for (const gift of this.freeGifts) {
      const baseTotal = this.getBaseTotal(workingCart);
      const targetQuantity = baseTotal >= gift.goal ? 1 : 0;
      const currentQuantity = this.getGiftQuantity(workingCart, gift);

      if (targetQuantity !== currentQuantity) {
        changed = (await this.setGiftQuantity(workingCart, gift, targetQuantity)) || changed;
      }
    }

    return { changed, issues: [] };
  }

  registerCheckoutVerifier() {
    const promoApi = window.SteakKingFreeGiftPromotions;
    if (!promoApi || typeof promoApi.registerCheckoutVerifier !== "function") {
      return false;
    }

    promoApi.registerCheckoutVerifier((context) => this.verifyCheckout(context));
    return true;
  }

  attachCheckoutEvent() {
    if (window.Shopify && Shopify.designMode) {
      return;
    }

    if (!this.freeGifts.length) {
      return;
    }

    const checkoutBtn = document.querySelector("[name='checkout']");
    if (checkoutBtn) {
      checkoutBtn.addEventListener(
        "click",
        async (event) => {
          event.preventDefault();
          await this.handleCheckoutProcess(checkoutBtn);
        },
        { capture: true, once: true }
      );
    }
  }

  hasZapietCheckoutManager() {
    return !!(
      document.querySelector(".zapiet-widget-wrapper") ||
      document.querySelector("#zapiet-checkout-methods") ||
      document.querySelector("[data-zapiet-checkout-button]") ||
      document.querySelector("[class*='zapiet-widget']")
    );
  }

  async handleCheckoutProcess(checkoutBtn) {
    const cart = await this.getCart();
    const baseTotal = this.getBaseTotal(cart);
    for (const gift of this.freeGifts) {
      if (
        baseTotal >= gift.goal &&
        !this.isProductInCart(cart, gift.product)
      ) {
        location.reload();
        return;
      }
    }

    if (checkoutBtn.form && typeof checkoutBtn.form.requestSubmit === "function") {
      checkoutBtn.form.requestSubmit(checkoutBtn);
      return;
    }

    checkoutBtn.form.submit();
  }
}
